/**
 * Google Analytics 4 Event Tracking Utility
 * Measurement ID: G-JJWPWDQSZM
 */

// Extend window to include gtag
declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
    dataLayer: unknown[];
  }
}

type GtagEventParams = Record<string, string | number | boolean | undefined>;

/**
 * Safe gtag wrapper - won't throw if gtag not loaded
 */
function gtag(...args: unknown[]): void {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag(...args);
  }
}

// ============================================
// USER EVENTS
// ============================================

/**
 * Track user signup
 */
export function trackSignup(method: 'google' = 'google'): void {
  gtag('event', 'sign_up', {
    method,
  });
}

/**
 * Track user login
 */
export function trackLogin(method: 'google' = 'google'): void {
  gtag('event', 'login', {
    method,
  });
}

/**
 * Track user logout
 */
export function trackLogout(): void {
  gtag('event', 'logout');
}

/**
 * Track child profile creation (onboarding completion)
 */
export function trackOnboardingComplete(childAge: number): void {
  gtag('event', 'tutorial_complete', {
    child_age: childAge,
  });
}

// ============================================
// GAME EVENTS
// ============================================

/**
 * Track game start
 */
export function trackGameStart(
  gameSlug: string,
  gameName: string,
  category?: string,
  isPremium: boolean = false
): void {
  gtag('event', 'game_start', {
    game_slug: gameSlug,
    game_name: gameName,
    game_category: category,
    is_premium: isPremium,
  });

  // Also track as level_start for gaming analytics
  gtag('event', 'level_start', {
    level_name: gameSlug,
  });
}

/**
 * Track game completion
 */
export function trackGameComplete(
  gameSlug: string,
  gameName: string,
  score?: number,
  timeSpentSeconds?: number
): void {
  gtag('event', 'game_complete', {
    game_slug: gameSlug,
    game_name: gameName,
    score,
    time_spent_seconds: timeSpentSeconds,
  });

  // Also track as level_end for gaming analytics
  gtag('event', 'level_end', {
    level_name: gameSlug,
    success: true,
  });
}

/**
 * Track game exit (before completion)
 */
export function trackGameExit(
  gameSlug: string,
  timeSpentSeconds?: number
): void {
  gtag('event', 'game_exit', {
    game_slug: gameSlug,
    time_spent_seconds: timeSpentSeconds,
  });
}

// ============================================
// CONTENT EVENTS
// ============================================

/**
 * Track content view (stimulasi, MPASI, dongeng, etc.)
 */
export function trackContentView(
  contentType: 'stimulasi' | 'mpasi' | 'dongeng' | 'tumbuh_kembang' | 'imunisasi',
  contentId: string,
  contentName: string
): void {
  gtag('event', 'view_item', {
    content_type: contentType,
    content_id: contentId,
    content_name: contentName,
  });
}

/**
 * Track favorite added
 */
export function trackAddToFavorites(
  itemType: 'game' | 'stimulasi' | 'dongeng',
  itemId: string,
  itemName: string
): void {
  gtag('event', 'add_to_wishlist', {
    item_type: itemType,
    item_id: itemId,
    item_name: itemName,
  });
}

// ============================================
// ECOMMERCE / PAYMENT EVENTS
// ============================================

type PackageType = 'monthly' | 'yearly' | 'fouryear';

const PACKAGE_PRICES: Record<PackageType, number> = {
  monthly: 20000,
  yearly: 50000,
  fouryear: 200000,
};

const PACKAGE_NAMES: Record<PackageType, string> = {
  monthly: 'Premium 1 Bulan',
  yearly: 'Premium Setahun',
  fouryear: 'Premium 4 Tahun',
};

/**
 * Track when user views premium page / pricing
 */
export function trackViewPricing(): void {
  gtag('event', 'view_item_list', {
    item_list_id: 'premium_packages',
    item_list_name: 'Premium Packages',
    items: [
      { item_id: 'monthly', item_name: 'Premium 1 Bulan', price: 20000 },
      { item_id: 'yearly', item_name: 'Premium Setahun', price: 50000 },
      { item_id: 'fouryear', item_name: 'Premium 4 Tahun', price: 200000 },
    ],
  });
}

/**
 * Track when user selects a premium package
 */
export function trackSelectPackage(packageType: PackageType): void {
  gtag('event', 'select_item', {
    item_list_id: 'premium_packages',
    items: [{
      item_id: packageType,
      item_name: PACKAGE_NAMES[packageType],
      price: PACKAGE_PRICES[packageType],
      currency: 'IDR',
    }],
  });
}

/**
 * Track when payment is initiated (QRIS shown)
 */
export function trackBeginCheckout(
  packageType: PackageType,
  totalAmount: number,
  discount: number = 0,
  voucherCode?: string
): void {
  gtag('event', 'begin_checkout', {
    currency: 'IDR',
    value: totalAmount,
    coupon: voucherCode,
    items: [{
      item_id: packageType,
      item_name: PACKAGE_NAMES[packageType],
      price: PACKAGE_PRICES[packageType],
      discount,
      quantity: 1,
    }],
  });
}

/**
 * Track successful payment - THIS IS A CONVERSION EVENT
 */
export function trackPurchase(
  orderId: string,
  packageType: PackageType,
  totalAmount: number,
  discount: number = 0,
  voucherCode?: string
): void {
  gtag('event', 'purchase', {
    transaction_id: orderId,
    currency: 'IDR',
    value: totalAmount,
    coupon: voucherCode,
    items: [{
      item_id: packageType,
      item_name: PACKAGE_NAMES[packageType],
      price: PACKAGE_PRICES[packageType],
      discount,
      quantity: 1,
    }],
  });

  // Also track as conversion
  gtag('event', 'conversion', {
    send_to: 'G-JJWPWDQSZM',
    transaction_id: orderId,
    value: totalAmount,
    currency: 'IDR',
  });
}

/**
 * Track failed/expired payment
 */
export function trackPaymentFailed(
  packageType: PackageType,
  reason: 'expired' | 'failed' | 'cancelled'
): void {
  gtag('event', 'payment_failed', {
    package_type: packageType,
    failure_reason: reason,
  });
}

// ============================================
// ENGAGEMENT EVENTS
// ============================================

/**
 * Track search
 */
export function trackSearch(searchTerm: string, resultsCount?: number): void {
  gtag('event', 'search', {
    search_term: searchTerm,
    results_count: resultsCount,
  });
}

/**
 * Track share
 */
export function trackShare(
  method: string,
  contentType: string,
  contentId: string
): void {
  gtag('event', 'share', {
    method,
    content_type: contentType,
    item_id: contentId,
  });
}

/**
 * Track premium upsell shown
 */
export function trackUpsellShown(trigger: string): void {
  gtag('event', 'upsell_shown', {
    trigger,
  });
}

/**
 * Track premium upsell clicked
 */
export function trackUpsellClicked(trigger: string): void {
  gtag('event', 'upsell_clicked', {
    trigger,
  });
}

// ============================================
// PAGE TRACKING
// ============================================

/**
 * Track page view (for SPA navigation)
 */
export function trackPageView(pagePath: string, pageTitle?: string): void {
  gtag('event', 'page_view', {
    page_path: pagePath,
    page_title: pageTitle,
  });
}

// ============================================
// USER PROPERTIES
// ============================================

/**
 * Set user properties for better segmentation
 */
export function setUserProperties(properties: {
  user_id?: string;
  is_premium?: boolean;
  child_count?: number;
  child_age?: number;
}): void {
  if (properties.user_id) {
    gtag('config', 'G-JJWPWDQSZM', {
      user_id: properties.user_id,
    });
  }

  gtag('set', 'user_properties', {
    is_premium: properties.is_premium,
    child_count: properties.child_count,
    child_age: properties.child_age,
  });
}

// ============================================
// EXCEPTION TRACKING
// ============================================

/**
 * Track errors/exceptions
 */
export function trackError(description: string, fatal: boolean = false): void {
  gtag('event', 'exception', {
    description,
    fatal,
  });
}
